import React from "react";
import { connect } from "react-redux";
import { Col, Row, Button, Overlay, Popover } from 'react-bootstrap';
import PropTypes from "prop-types";
import { NumericFormat as NumberFormat } from "react-number-format";
import { v4 as uuidv4 } from "uuid";

import { withNavigation } from "../../../utils/wrapper";
import Table from "../../../layout/table";
import Hamburger from "../../../layout/kebab";
import { search, batch } from "../../../actions/matter.staff";
import { isGuid } from "../../../utils/string";

const positiveNumberOrNull = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
};

class Assignment extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            page: 0,
            size: 10,
            search: "",
            order: "lastName",
            index: 0,
            direction: "DESC",
            data: [],
            pages: 0,
            menu: {}
        };
    }

    componentDidMount = async () => {
        await this.loadData();
    }

    loadData = async () => {
        const __data = await this.props.search(
            {
                page: this.state.page,
                size: this.state.size,
                order: this.state.order,
                direction: this.state.direction,
                assignment: "case",
                uuid: this.props.owner,
                search: this.state.search
            });
        let __count = 0;

        if ((__data?.items?.length || 0) > 0) {
            __count = Math.ceil(Number(__data.count) / Number(this.state.size));
        }

        this.setState({
            data: __data.items ?? [],
            pages: __count
        });
    }

    buildSavePayload = () => {
        const changed = (this.state.data || []).filter(r => !!r.changed);

        return changed.map(r => {
            const __rate = this.resolveRate(r);
            return (
            
                {
                    uuid: r["Staff.Staff_CaseStaff.uuid"] || null,
                    staffUuid: r["Staff.uuid"] || r.uuid,
                    caseStaffUuid: r["Staff.Staff_CaseStaff.uuid"] || null,
                    oldCaseStaffUuid: r["old_CaseStaff.uuid"] || null,
                    assignmentDate: r["Staff.Staff_CaseStaff.assignmentDate"] || Date.now(),
                    removalDate: r["Staff.Staff_CaseStaff.removalDate"] || null,
                    rate: __rate,
                    minutes: r["Staff.Staff_CaseStaff.minutes"] ?? null,

                    // optional helpers if you use them server-side:
                    new_assignment: !!r.new_assignment,
                    original_assignment: r.original_assignment ?? null
                })
        });
    };

    resolveRate = (row) => {
        const direct = row["Staff.Staff_CaseStaff.rate"] ?? row["CaseStaff.rate"];
        const directRate = positiveNumberOrNull(direct);
        if (directRate != null) return directRate;

        const code = row["Staff.rateCode"];
        const match = this.props.rates?.find(x => x.code === code);
        return positiveNumberOrNull(match?.value);
    };

    // This is what the parent will call via ref
    saveChanges = async () => {
        const items = this.buildSavePayload();
        if (!items.length) return { saved: 0, message: "No changes." };

        // optional: disable UI while saving
        this.setState({ saving: true, saveError: null });

        try {
            const result = await this.props.batch({
                caseUuid: this.props.owner,
                items
            });

            // clear flags locally after success
            this.setState(prev => ({
                saving: false,
                data: (prev.data || []).map(r => (r.changed ? { ...r, changed: false } : r))
            }));

            // optional: reload from server to normalize everything
            await this.loadData();

            return { saved: items.length, result };
        } catch (err) {
            this.setState({ saving: false, saveError: err });
            throw err;
        }
    };

    onEmployeeFormat = (e, row) => {
        let __current = isGuid(row["Staff.Staff_CaseStaff.uuid"]);
        if (__current) {
            const ad = row["Staff.Staff_CaseStaff.assignmentDate"];
            __current = (ad ? new Date(ad).getTime() : 0) < Date.now() && !row["Staff.Staff_CaseStaff.removalDate"];
        }

        return (<>
            <Button variant="link" onClick={() => this.props.navigate(`/dashboard/people/${row["Staff.uuid"]}`)}>
                {row["lastName"]}, {row["firstName"]} ({row["Staff.title"]})
            </Button >
            <button
                type="button"
                className={`badge rounded-pill bg-${__current ? "success" : "danger"}`}
                style={{ marginLeft: 5 }}
                onClick={() => this.onAssign({ uuid: row.uuid, assigned: __current })}
            >
                {__current ? "ASSIGNED" : "UNASSIGNED"}
            </button>
        </>);
    }
    
    onRateFormat = (e, row) => {
        const __rate = this.resolveRate(row);
        return (<>
            <NumberFormat
                displayType="input"
                type="text"
                id={`rate-${row["uuid"]}`}
                name="rate"
                className="text-right form-control mb-2"
                prefix={"$"}
                thousandSeparator={true}
                allowNegative={false}
                value={__rate}
                onValueChange={(e) =>
                    this.onRateChange({
                        uuid: row["uuid"],
                        rate: e.floatValue,
                        type: "rate"
                    })
                }
                removeFormatting
            /></>);
    }

    onMinutesFormat = (e, row) => {
        const __minutes = row["Staff.Staff_CaseStaff.minutes"] ?? this.props.minutes ?? 6;
        return (<>
            <NumberFormat
                displayType="input"
                type="text"
                id={`minutes-${row["uuid"]}`}
                name="minutes"
                className="text-right form-control mb-2"
                suffix={" mns."}
                thousandSeparator={true}
                allowNegative={false}
                value={__minutes}
                onValueChange={(e) =>
                    this.onMinutesChange({
                        uuid: row["uuid"],
                        minutes: e.floatValue,
                        type: "minutes"
                    })
                }
                removeFormatting
                isAllowed={(values) => {
                    const { floatValue } = values;
                    return floatValue <= 60;
                }}
            /></>);
    }

    onAssign = ({ uuid, assigned }) => {
        let didChange = false;
        let nextData = null;

        this.setState(prev => {
            nextData = prev.data.map(row => {
                if (row.uuid !== uuid) return row;

                const wasAssigned = !!assigned;
                const willBeAssigned = !wasAssigned;

                const prevCaseStaffUuid = row["Staff.Staff_CaseStaff.uuid"] ?? null;

                // Decide new values
                const nextCaseStaffUuid =
                    willBeAssigned
                        ? (isGuid(prevCaseStaffUuid) ? prevCaseStaffUuid : uuidv4())
                        : null;

                const nextAssignmentDate =
                    willBeAssigned
                        ? (row["Staff.Staff_CaseStaff.assignmentDate"] ?? Date.now())
                        : row["Staff.Staff_CaseStaff.assignmentDate"];

                const nextRemovalDate =
                    willBeAssigned
                        ? null
                        : (row["Staff.Staff_CaseStaff.removalDate"] ?? Date.now());

                // If nothing actually changes, keep the same row object
                const noNetChange =
                    (prevCaseStaffUuid ?? null) === (nextCaseStaffUuid ?? null) &&
                    (row["Staff.Staff_CaseStaff.assignmentDate"] ?? null) === (nextAssignmentDate ?? null) &&
                    (row["Staff.Staff_CaseStaff.removalDate"] ?? null) === (nextRemovalDate ?? null);

                if (noNetChange) return row;

                didChange = true;

                return {
                    ...row,
                    ["old_CaseStaff.uuid"]: !willBeAssigned ? prevCaseStaffUuid : (row["old_CaseStaff.uuid"] ?? null),
                    ["Staff.Staff_CaseStaff.uuid"]: nextCaseStaffUuid,
                    ["Staff.Staff_CaseStaff.assignmentDate"]: nextAssignmentDate,
                    ["Staff.Staff_CaseStaff.removalDate"]: nextRemovalDate,
                    new_assignment: row["new_assignment"] || (willBeAssigned && !isGuid(prevCaseStaffUuid)),
                    original_assignment: row["original_assignment"] ?? wasAssigned,
                    changed: true,
                    isNew: willBeAssigned && !isGuid(prevCaseStaffUuid)
                };
            });

            return didChange ? { data: nextData } : null;
        }, () => {
            if (didChange) this.onChange(nextData);
        });
    }


    onRateChange = ({ uuid, rate }) => {
        let didChange = false;
        let nextData = null;

        this.setState(prev => {
            nextData = prev.data.map(row => {
                if (row.uuid !== uuid) return row;

                const prevRate = row["Staff.Staff_CaseStaff.rate"] ?? null;
                const nextRate = rate ?? null;

                if (Object.is(prevRate, nextRate)) return row; // no change

                didChange = true;
                return {
                    ...row,
                    ["Staff.Staff_CaseStaff.rate"]: nextRate,
                    changed: true
                };
            });

            return didChange ? { data: nextData } : null;
        }, () => {
            if (didChange) this.onChange(nextData);
        });
    };

    onMinutesChange = ({ uuid, minutes }) => {
        let didChange = false;
        let nextData = null;

        this.setState(prev => {
            nextData = prev.data.map(row => {
                if (row.uuid !== uuid) return row;

                const prevMinutes = row["Staff.Staff_CaseStaff.minutes"] ?? null;
                const nextMinutes = minutes ?? null;

                if (Object.is(prevMinutes, nextMinutes)) return row; // no change

                didChange = true;
                return {
                    ...row,
                    ["Staff.Staff_CaseStaff.minutes"]: nextMinutes,
                    changed: true
                };
            });

            return didChange ? { data: nextData } : null;
        }, () => {
            if (didChange) this.onChange(nextData);
        });
    };

    onAssignmentCaption = (row) => {
        let __current = isGuid(row["Staff.Staff_CaseStaff.uuid"]);
        if (__current) {
            const ad = row["Staff.Staff_CaseStaff.assignmentDate"];
            __current = (ad ? new Date(ad).getTime() : 0) < Date.now() && !row["Staff.Staff_CaseStaff.removalDate"];
        }
        return __current ? "Unassign" : "Assign";
    }

    onToggleAssignment = (row) => {
        let __current = isGuid(row["Staff.Staff_CaseStaff.uuid"]);
        if (__current) {
            const ad = row["Staff.Staff_CaseStaff.assignmentDate"];
            __current = (ad ? new Date(ad).getTime() : 0) < Date.now() && !row["Staff.Staff_CaseStaff.removalDate"];
        }
        this.onAssign({ uuid: row.uuid, assigned: __current });
        this.closeHamburger();
    }

    onChange = (data) => {
        if (this.props.onChange) this.props.onChange(data);
    };

    openHamburger = (event, row) => {
        event.preventDefault();
        this.setState({
            menu: {
                show: true,
                row,
                target: event.currentTarget
            }
        });
    };
    
    closeHamburger = () => {
        this.setState(prev => ({
            menu: {
                ...prev.menu,
                show: false,
                target: null
            }
        }));
    };
    
    onActionFormat = (e, row) => {
        return (<>
            <Hamburger variant="link" vertical={false} onClick={(e) => this.openHamburger(e, row)} />
        </>)
    };

    onPageChange = (e) => {
        this.setState({
            page: Number(e)
        }, this.loadData);
    }

    onColumnClick = (e) => {
        const __array = this.headers;
        const __index = __array.findIndex((item) => {
            return item.header === e.header && item.source === e.source;
        });

        this.setState({
            index: __index,
            direction: this.state.index === __index && this.state.direction === "DESC" ? "ASC" : "DESC",
            order: __array[__index].source,
            page: 0
        }, this.loadData);
    }

    onSearch = (e) => {
        this.setState({
            search: e
        }, this.loadData);
    }

    onSize = (e) => {
        this.setState({
            page: 0,
            size: Number(e)
        }, this.loadData);
    }
    
    get headers() {
        return [
            {
                header: "Staff",
                sortable: true,
                source: "lastName",
                className: "text-muted text-small text-uppercase w-70",
                onRender: this.onEmployeeFormat
            },
            {
                header: "Interval (Minutes)",
                sortable: true,
                source: "uuid",
                className: "text-muted text-small text-uppercase w-15",
                onRender: this.onMinutesFormat
            },
            {
                header: "Rate",
                sortable: true,
                source: "uuid",
                className: "text-muted text-small text-uppercase w-15",
                onRender: this.onRateFormat
            },
            {
                header: "",
                sortable: false,
                source: "uuid",
                onRender: this.onActionFormat
            }
        ]
    }



    render() {
        return (
            <>
                <Row className="g-3">
                    <Col xs="12">
                        <h2 className="small-title">Assignment</h2>
                    </Col>
                    <Col xs="12">
                        <Table
                            headers={this.headers}
                            page={this.state.data}
                            sort={{ index: this.state.index, direction: this.state.direction }}
                            size={this.state.size}
                            index={this.state.page}
                            pages={this.state.pages}
                            onColumnClick={this.onColumnClick}
                            onPageChange={this.onPageChange}
                            onSearch={this.onSearch}
                            onSize={this.onSize}
                            searchable
                            sizable
                        />
                    </Col>
                </Row>
                <Overlay
                    target={this.state.menu.target}
                    show={this.state.menu.show}
                    placement="bottom-end"
                    rootClose
                    onHide={this.closeHamburger}
                >
                    {(props) => (
                        <Popover {...props}>
                                           
                            <Popover.Body>
                                <Row className="mb-2">
                                    <Col className="d-flex flex-row mt-1">
                                               
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={() => this.props.navigate(`/dashboard/people/${this.state.menu.row["Staff.uuid"]}`)}
                                        >
                                            Open
                                        </button></Col></Row><Row>
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={() => this.onToggleAssignment(this.state.menu.row)}
                                        >
                                            {this.onAssignmentCaption(this.state.menu.row)}
                                        </button></Col>
                                </Row>
                            </Popover.Body>
                        </Popover>
                    )}
                </Overlay>
            </>
        )
    }
}

Assignment.propTypes = {
    auth: PropTypes.object.isRequired
}

const mapStateToProps = (state) => {
    return ({
        auth: state.auth
    })
}


export default withNavigation(
    connect(
        mapStateToProps,
        {
            search,
            batch
        },
        null,
        { forwardRef: true }
    )(Assignment));
